/**
 * MobilePaywall
 *
 * Native-only paywall. Rendered instead of GoatUpgradeModal when isNative.
 * Routes purchases through RevenueCat (Apple StoreKit / Google Play Billing)
 * via window.electron.iap.
 *
 * When it opens (all of them the user's own tap, except the last):
 *   - a free wall inside a feature — For You, Mixpedia "See all N results",
 *     Crate Digger, Set Planner, Notifications. The wall passes what the user
 *     was looking at (`upgradeContext`: moreCount / total / list) and the
 *     headline names it (utils/paywall.js);
 *   - the trial banner / the Subscription tab (generic headline);
 *   - the trial-end sheet: App.js presents it ONCE per account the first time
 *     the account is seen on Free with its 14-day trial expired
 *     (utils/trialEndSheet.js). It is the only auto-presented opening and it
 *     is closable from the first frame — there is no delayed or hidden close
 *     anywhere in this sheet (Apple rejects those under 3.1.2).
 *
 * Layout: close (×) → logo → headline (+ subline on the trial-end sheet) →
 * benefits → yearly/monthly cards (yearly default, savings badge) → ONE
 * primary CTA → fine print with the selected plan's price → restore →
 * Terms / Privacy. The trial-end sheet adds a plain "Keep the Free plan".
 *
 * The free-trial wording only appears when RevenueCat reports a free
 * introductory offer on the product AND the account never had the server
 * trial. Without an offer it falls back to "Subscribe" so it never claims a
 * trial StoreKit wouldn't honor.
 *
 * Apple guideline 3.1.1 forbids steering users to external payment, so this
 * modal must NOT mention Stripe, web upgrades, or external billing URLs.
 *
 * Web/Electron continue to use GoatUpgradeModal (unchanged).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { refreshTier, confirmTierAfterPurchase } from '../redux/thunk/tierThunk';
import { hideUpgradeModal, setTierInfo } from '../redux/slices/tierSlice';
import MixGoatLogo from './MixGoatLogo';
import ErrorBanner from './ErrorBanner';
import { track } from '../utils/analytics';
import { paywallHeadline, paywallSubline, TRIAL_ENDED_FEATURE } from '../utils/paywall';

const ACCENT = '#00bcd4';

// Detect a free-trial introductory offer on a package. RevenueCat surfaces it
// as product.introPrice with a zero price. Returns { n, unit, label, cta }.
function freeTrialOf(pkg) {
  const intro = pkg?.product?.introPrice;
  if (!intro) return null;
  const isFree = intro.price === 0 || intro.price === '0' || /free|^0/i.test(String(intro.priceString || ''));
  if (!isFree) return null;
  const n = intro.periodNumberOfUnits || 14;
  const unit = String(intro.periodUnit || 'day').toLowerCase().replace(/s$/, '');
  const Unit = unit.charAt(0).toUpperCase() + unit.slice(1);
  return {
    n,
    unit,
    label: `${n} ${unit}${n === 1 ? '' : 's'}`, // "14 days"
    cta: `${n}-${Unit}`,                          // "14-Day"
  };
}

// Yearly savings vs paying monthly, when both numeric prices are available.
function pctSavings(annualPkg, monthlyPkg) {
  const a = annualPkg?.product?.price;
  const m = monthlyPkg?.product?.price;
  if (typeof a === 'number' && typeof m === 'number' && m > 0) {
    const pct = Math.round((1 - a / (m * 12)) * 100);
    return pct > 0 ? pct : null;
  }
  return null;
}

// The `more_count` analytics property: whichever count the wall handed over.
function contextCount(context) {
  const n = Number(context?.moreCount ?? context?.total);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function MobilePaywall() {
  const dispatch = useDispatch();
  const showModal = useSelector((s) => s.tier?.showUpgradeModal);
  const [offerings, setOfferings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('annual'); // 'annual' | 'monthly'
  const upgradeFeature = useSelector((s) => s.tier?.upgradeFeature);
  const upgradeContext = useSelector((s) => s.tier?.upgradeContext);
  const isTrialEnded = upgradeFeature === TRIAL_ENDED_FEATURE;

  // If the user already consumed a trial on another platform (server trial —
  // trialStartDate is set), don't offer a second free trial on iOS. Only
  // legacy iOS-origin accounts have no server trial (trialStartDate null) →
  // they may still get the StoreKit trial. This makes "trial only for
  // genuinely-new users" hold across systems.
  const priorServerTrial = useSelector((s) => !!s.tier?.trialStartDate);

  useEffect(() => {
    if (!showModal) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await window.electron.iap.getOfferings();
        if (cancelled) return;
        if (res && !res.error) setOfferings(res);
        else setError(res?.error || 'Failed to load subscription options');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load subscription options');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;
    track('upgrade_modal_shown', { feature: upgradeFeature || null, paywall: 'native', more_count: contextCount(upgradeContext) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const close = useCallback(() => {
    track('upgrade_modal_dismissed', { feature: upgradeFeature || null, paywall: 'native' });
    dispatch(hideUpgradeModal());
  }, [dispatch, upgradeFeature]);

  const purchase = useCallback(async (pkg) => {
    if (!pkg) {
      setError('Subscription option unavailable. Try again in a moment.');
      return;
    }
    setPurchasing(true);
    setError(null);
    track('subscribe_clicked', { feature: upgradeFeature || null, source: 'native_paywall', plan: selectedPlan });
    try {
      const res = await window.electron.iap.purchase(pkg);
      if (res?.cancelled) return;
      if (res?.error) { setError(res.error); return; }
      // Optimistically flip to GOAT — RC confirmed locally. The server webhook
      // arrives 1-3s later; confirmTierAfterPurchase() POLLS /reconciled and only
      // applies once it confirms GOAT, so we never flash back to 'free' (a single
      // immediate refreshTier would race the webhook and downgrade transiently).
      dispatch(setTierInfo({ tier: 'goat', effectiveTier: 'goat', reason: 'iap',
        trialInfo: { trialStartDate: null, trialEndDate: null, daysRemaining: 0, isTrialActive: false, isTrialExpired: false } }));
      dispatch(hideUpgradeModal());
      dispatch(confirmTierAfterPurchase());
    } catch (err) {
      setError(err?.message || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  }, [dispatch, upgradeFeature, selectedPlan]);

  const restore = useCallback(async () => {
    setRestoring(true);
    setError(null);
    try {
      const res = await window.electron.iap.restorePurchases();
      if (res?.error) { setError(res.error); return; }
      const hasGoat = !!(res?.customerInfo?.entitlements?.active?.goat);
      if (hasGoat) {
        dispatch(setTierInfo({ tier: 'goat', effectiveTier: 'goat', reason: 'iap',
          trialInfo: { trialStartDate: null, trialEndDate: null, daysRemaining: 0, isTrialActive: false, isTrialExpired: false } }));
        dispatch(hideUpgradeModal()); // close the paywall on a successful restore
        dispatch(confirmTierAfterPurchase()); // poll until the webhook confirms; no flash-to-free
      } else {
        setError('No active subscription found to restore.');
        dispatch(refreshTier());
      }
    } catch (err) {
      setError(err?.message || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  }, [dispatch]);

  if (!showModal) return null;

  const monthlyPkg = offerings?.current?.monthly || null;
  const annualPkg = offerings?.current?.annual || null;
  const annualPrice = annualPkg?.product?.priceString || '$89.99';
  const monthlyPrice = monthlyPkg?.product?.priceString || '$9.99';

  // Only surface the StoreKit free trial to users who haven't already had one
  // elsewhere (priorServerTrial). Returning-trial users see plain "Subscribe".
  const annualTrial = priorServerTrial ? null : freeTrialOf(annualPkg);
  const monthlyTrial = priorServerTrial ? null : freeTrialOf(monthlyPkg);
  const savings = pctSavings(annualPkg, monthlyPkg);

  const isAnnual = selectedPlan === 'annual';
  const selectedPkg = isAnnual ? annualPkg : monthlyPkg;
  const selectedTrial = isAnnual ? annualTrial : monthlyTrial;
  const selectedPriceLine = isAnnual ? `${annualPrice}/year` : `${monthlyPrice}/month`;

  const headline = paywallHeadline({ feature: upgradeFeature, context: upgradeContext, trialLabel: selectedTrial?.label || null });
  const subline = paywallSubline(upgradeFeature);
  const ctaLabel = purchasing
    ? 'Processing…'
    : (selectedTrial ? `Start ${selectedTrial.cta} Free Trial` : (isTrialEnded ? 'Keep GOAT' : 'Subscribe to GOAT'));
  const finePrint = selectedTrial
    ? `Free for ${selectedTrial.label}, then ${selectedPriceLine}. Cancel anytime.`
    : `${selectedPriceLine}. Auto-renews until cancelled.`;

  const closeOnBackdrop = (e) => { if (e.target === e.currentTarget) close(); };

  const PlanCard = ({ plan, price, perLabel, badge, trial }) => {
    const active = selectedPlan === plan;
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setSelectedPlan(plan)}
        style={{
          flex: 1, textAlign: 'left', cursor: 'pointer',
          boxSizing: 'border-box', userSelect: 'none',
          padding: '14px', borderRadius: 12,
          border: `2px solid ${active ? ACCENT : 'rgba(255,255,255,0.18)'}`,
          background: active ? 'rgba(0,188,212,0.12)' : 'transparent',
          color: '#fff', position: 'relative',
          display: 'flex', flexDirection: 'column', gap: 4,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
            {plan === 'annual' ? 'Yearly' : 'Monthly'}
          </span>
          <span style={{
            width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
            border: `2px solid ${active ? ACCENT : 'rgba(255,255,255,0.4)'}`,
            background: active ? ACCENT : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {active && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#000' }} />}
          </span>
        </span>
        <span style={{ fontSize: 18, fontWeight: 700 }}>{price}<span style={{ fontSize: 12, fontWeight: 500, opacity: 0.7 }}>{perLabel}</span></span>
        <span style={{ fontSize: 11, lineHeight: '14px', minHeight: 14, color: ACCENT }}>
          {trial ? `${trial.label} free` : ' '}
        </span>
        {badge && (
          <span style={{
            position: 'absolute', top: -9, right: 10,
            background: ACCENT, color: '#000', fontSize: 10, fontWeight: 800,
            padding: '2px 7px', borderRadius: 8, letterSpacing: 0.3,
          }}>{badge}</span>
        )}
      </div>
    );
  };

  return (
    <div
      className="mobile-paywall-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={closeOnBackdrop}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        className="mobile-paywall-sheet"
        data-feature={upgradeFeature || ''}
        style={{
          position: 'relative', width: '100%', maxWidth: 480,
          background: '#1a1a2e', color: '#fff',
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          padding: '24px 20px calc(env(safe-area-inset-bottom, 0) + 24px)',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        {/* Always present, from the first frame — no delayed or hidden close. */}
        <button
          type="button" aria-label="Close" onClick={close}
          style={{
            position: 'absolute', top: 12, right: 12, width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.08)', color: '#fff', border: 'none',
            borderRadius: '50%', fontSize: 18, lineHeight: 1, cursor: 'pointer',
          }}
        >×</button>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }} aria-hidden>
          <MixGoatLogo width={52} height={52} />
        </div>

        <h2 style={{ margin: 0, paddingInline: 24, fontSize: 21, fontWeight: 700, textAlign: 'center' }}>
          {headline}
        </h2>

        {subline && (
          <p style={{ margin: '-4px 0 0', paddingInline: 8, fontSize: 14, lineHeight: 1.45, textAlign: 'center', opacity: 0.8 }}>
            {subline}
          </p>
        )}

        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
          <li>✓ Full Mixpedia — every DJ, set &amp; track</li>
          <li>✓ Your full For You feed</li>
          <li>✓ Crate Digger — full lists, unlimited playlists</li>
          <li>✓ Set Planner — unlimited steps, sets &amp; export</li>
          <li>✓ Cloud Backup, loudness match, metadata repair &amp; organize <span style={{ opacity: 0.55 }}>(desktop)</span></li>
          <li>✓ One subscription for your phone and your desktop</li>
        </ul>

        {error && <ErrorBanner variant="inline" error={error} surface="paywall" />}

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <PlanCard plan="annual" price={annualPrice} perLabel="/yr" trial={annualTrial}
            badge={savings ? `SAVE ${savings}%` : null} />
          <PlanCard plan="monthly" price={monthlyPrice} perLabel="/mo" trial={monthlyTrial} badge={null} />
        </div>

        <button
          type="button"
          disabled={loading || purchasing || !selectedPkg}
          onClick={() => purchase(selectedPkg)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: 54, padding: '0 16px', borderRadius: 12, border: 'none',
            background: ACCENT, color: '#000', fontSize: 17, fontWeight: 800,
            lineHeight: 1, cursor: selectedPkg ? 'pointer' : 'not-allowed',
            opacity: (purchasing || !selectedPkg) ? 0.6 : 1, marginTop: 2,
          }}
        >
          {ctaLabel}
        </button>

        <div style={{ fontSize: 12, opacity: 0.75, textAlign: 'center', lineHeight: 1.5 }}>
          {finePrint}
        </div>

        {isTrialEnded && (
          <button
            type="button"
            onClick={close}
            style={{
              background: 'transparent', border: 'none', color: '#c2cad4',
              fontSize: 15, fontWeight: 600, minHeight: 36, padding: '6px 16px', cursor: 'pointer',
            }}
          >
            Keep the Free plan
          </button>
        )}

        <button
          type="button" disabled={restoring} onClick={restore}
          style={{ padding: '6px', border: 'none', background: 'transparent', color: '#bbb', fontSize: 13, cursor: 'pointer' }}
        >
          {restoring ? 'Restoring…' : 'Restore purchases'}
        </button>

        {/* Apple requires Terms + Privacy links on subscription paywalls. */}
        <div style={{ fontSize: 11, opacity: 0.6, textAlign: 'center', lineHeight: 1.5 }}>
          <span style={{ textDecoration: 'underline' }} onClick={() => window.electron.openUrl('https://mixgoat.com/terms')}>Terms</span>
          {' '}·{' '}
          <span style={{ textDecoration: 'underline' }} onClick={() => window.electron.openUrl('https://mixgoat.com/privacy')}>Privacy Policy</span>
          {' '}· Cancel anytime in {window.Capacitor?.getPlatform?.() === 'ios' ? 'Settings → Apple ID' : 'Google Play'}
        </div>
      </div>
    </div>
  );
}

export default MobilePaywall;
